// src/App.js (or WrappedApp.js)
import React, { Suspense } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  useLocation,
} from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { useMotion } from "./ui/motion";
import ScrollToTop from "./Utils/ScrollToTop";
import Navbar from "./components/Navbar/Navbar";
import Contact from "./components/Contact/Contact";
import Footer from "./components/Footer/Footer";
import Farms from "./components/Farms/Farms";
import FarmBaskets from "./components/Farms/FarmBaskets/FarmBaskets";
import BasketPage from "./components/Baskets/BasketPage";
import BasketsList from "./components/Baskets/BasketsList";
import AccountSettings from "./components/Account/AccountSettings";
import LoginPage from "./components/Login/Login-Signup-pages/LoginPage";
import SignupPage from "./components/Login/Login-Signup-pages/SignupPage";
import ForgotPasswordPage from "./components/Login/Login-Signup-pages/ForgotPasswordPage";
import ResetPasswordPage from "./components/Login/Login-Signup-pages/ResetPasswordPage";
import ConfirmEmailPage from "./components/Login/Login-Signup-pages/ConfirmEmailPage";
import ProductList from "./components/Products/ProductList";
import ProductPage from "./components/Products/ProductPage";
import NotFound from "./components/NotFound/NotFound";
import Cart from "./components/Cart/Cart";
import CartButtonWrapper from "./components/Cart/CartButtonWrapper";
import CartDrawer from "./components/Cart/CartDrawer";
import CartSheet from "./components/Cart/CartSheet";
import { CartProvider } from "./context/CartContext";
import { CartDrawerProvider } from "./context/CartDrawerContext";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { FarmProvider } from "./context/FarmContext";
import { AuthPromptProvider } from "./context/AuthPromptContext";
import AuthPromptDialog from "./components/Auth/AuthPromptDialog";
import PublicRoute from "./Utils/PublicRoute";
import PrivateRoute from "./Utils/PrivateRoute";
import AdminRoute from "./Utils/AdminRoute";
import VillagerRoute from "./Utils/VillagerRoute";
import About from "./components/About/About";
import HomepageMarketplace from "./components/HomepageMarketplace/HomepageMarketplace";
import WelcomeDiscount from "./components/WelcomeDiscount/WelcomeDiscount";
import CookieConsent from "./components/CookieConsent/CookieConsent";
import ErrorBoundary from "./ui/ErrorBoundary";
import OfflineIndicator from "./components/OfflineIndicator/OfflineIndicator";
import PushPrompt from "./components/PushPrompt/PushPrompt";
import SEO from "./ui/SEO";
import JsonLd from "./ui/JsonLd";
import { useToast } from "./context/ToastContext";
import { setupAxiosInterceptor } from "./Utils/axiosInterceptor";
import { getHomeRouteForRole } from "./Utils/homeRoute";
import { useTranslation } from "react-i18next";
import "./App.css";

// Lazy-loaded heavy routes
const Dashboard = React.lazy(() => import("./components/Dashboard/Dashboard"));
const AdminDashboard = React.lazy(() => import("./components/Admin/AdminDashboard"));
const VillagerPortal = React.lazy(() => import("./components/VillagerPortal/VillagerPortal"));
const Checkout = React.lazy(() => import("./components/Checkout/Checkout"));
const FarmerOnboarding = React.lazy(() => import("./components/Onboarding/FarmerOnboarding"));
const FindMyBasket = React.lazy(() => import("./components/FindMyBasket/FindMyBasket"));
const OrderDetail = React.lazy(() => import("./components/OrderDetail/OrderDetail"));
const PrivacyPolicy = React.lazy(() => import("./components/Legal/PrivacyPolicy"));
const TermsOfService = React.lazy(() => import("./components/Legal/TermsOfService"));

const LazyFallback = () => (
  <div style={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "60vh" }}>
    <div style={{
      width: 36, height: 36,
      border: "3px solid var(--color-accent-primary)",
      borderTopColor: "transparent",
      borderRadius: "50%",
      animation: "spin 0.7s linear infinite",
    }} />
    <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
  </div>
);

const AxiosSetup = () => {
  const { error } = useToast();
  const { logout } = useAuth();
  React.useEffect(() => {
    setupAxiosInterceptor(error, logout);
  }, [error, logout]);
  return null;
};

const LangSync = () => {
  const { i18n } = useTranslation();
  React.useEffect(() => {
    const languageCodeMap = {
      al: "sq",
      us: "en",
    };
    const resolvedLanguage = i18n.resolvedLanguage || i18n.language;
    document.documentElement.lang = languageCodeMap[resolvedLanguage] || "en";
  }, [i18n.language, i18n.resolvedLanguage]);
  return null;
};

const PageWrapper = ({ children }) => {
  const { reduce, d } = useMotion();
  // Opacity-only cross-fade: in-page content owns directional motion, so the
  // page transition shouldn't also translate (avoids a double "woosh").
  return (
    <motion.main
      id="main-content"
      tabIndex={-1}
      style={{ display: "flex", flexDirection: "column", minHeight: "100vh" }}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: reduce ? 0 : d("base") }}
    >
      {children}
    </motion.main>
  );
};

const App = () => {
  const location = useLocation();
  const { userRole } = useAuth();
  const { t } = useTranslation();

  return (
    <>
      <a className="skip-link" href="#main-content">
        {t("common.skip_to_content", { defaultValue: "Skip to content" })}
      </a>
      <AxiosSetup />
      <LangSync />
      <OfflineIndicator />
      <PushPrompt />
      <SEO />
      <JsonLd />
      <ScrollToTop />
      <WelcomeDiscount />
      <CookieConsent />
      <Suspense fallback={<LazyFallback />}>
      <AnimatePresence mode="wait">
        <Routes location={location} key={location.pathname}>
          {/* Public Routes: Prevent logged-in users from accessing these */}
          <Route element={<PublicRoute />}>
            <Route
              path="/login"
              element={
                <PageWrapper>
                  <Navbar />
                  <LoginPage />
                  <Footer />
                </PageWrapper>
              }
            />
          <Route
            path="/signup"
            element={
              <PageWrapper>
                <Navbar />
                <SignupPage />
                <Footer />
              </PageWrapper>
            }
          />
          <Route
            path="/forgot-password"
            element={
              <PageWrapper>
                <Navbar />
                <ForgotPasswordPage />
                <Footer />
              </PageWrapper>
            }
          />
          <Route
            path="/reset-password"
            element={
              <PageWrapper>
                <Navbar />
                <ResetPasswordPage />
                <Footer />
              </PageWrapper>
            }
          />
          <Route
            path="/confirm-email"
            element={
              <PageWrapper>
                <Navbar />
                <ConfirmEmailPage />
                <Footer />
              </PageWrapper>
            }
          />
        </Route>

          <Route element={<PrivateRoute />}>
            <Route
              path="/dashboard"
              element={
                <PageWrapper>
                  <Dashboard />
                </PageWrapper>
              }
            />
            <Route
              path="/profile"
              element={<Navigate to={getHomeRouteForRole(userRole)} replace />}
            />
            <Route
              path="/account"
              element={
                <PageWrapper>
                  <Navbar />
                  <ErrorBoundary>
                    <AccountSettings />
                  </ErrorBoundary>
                  <Footer />
                </PageWrapper>
              }
            />
            <Route
              path="/orders/:id"
              element={
                <PageWrapper>
                  <OrderDetail />
                </PageWrapper>
              }
            />
          </Route>
          <Route element={<VillagerRoute />}>
            <Route
              path="/villager"
              element={
                <PageWrapper>
                  <VillagerPortal />
                </PageWrapper>
              }
            />
            <Route
              path="/farmer-onboarding"
              element={
                <PageWrapper>
                  <FarmerOnboarding />
                </PageWrapper>
              }
            />
          </Route>
          <Route
            path="/products"
            element={
              <PageWrapper>
                <ProductList />
              </PageWrapper>
            }
          />
          <Route
            path="/products/:id"
            element={
              <PageWrapper>
                <Navbar />
                <ProductPage />
                <Footer />
              </PageWrapper>
            }
          />
          <Route element={<AdminRoute />}>
            <Route
              path="/admin"
              element={
                <PageWrapper>
                  <ErrorBoundary>
                    <AdminDashboard />
                  </ErrorBoundary>
                </PageWrapper>
              }
            />
          </Route>

          {/* Routes accessible to all users */}
          <Route
            path="/"
            element={
              <PageWrapper>
                <Navbar />
                <HomepageMarketplace />
                <About />
                <Contact />
                <Footer />
              </PageWrapper>
            }
          />
          <Route
            path="/farms"
            element={
              <PageWrapper>
                <Navbar />
                <ErrorBoundary>
                  <Farms />
                </ErrorBoundary>
                <Footer />
              </PageWrapper>
            }
          />
          <Route
            path="/farms/:id"
            element={
              <PageWrapper>
                <Navbar />
                <FarmBaskets />
                <Footer />
              </PageWrapper>
            }
          />
          <Route
            path="/baskets"
            element={
              <PageWrapper>
                <Navbar />
                <ErrorBoundary>
                  <BasketsList />
                </ErrorBoundary>
                <Footer />
              </PageWrapper>
            }
          />
          <Route
            path="/baskets/:id"
            element={
              <PageWrapper>
                <Navbar />
                <BasketPage />
                <Footer />
              </PageWrapper>
            }
          />
          <Route
            path="/find-my-basket"
            element={
              <PageWrapper>
                <Navbar />
                <ErrorBoundary>
                  <FindMyBasket />
                </ErrorBoundary>
                <Footer />
              </PageWrapper>
            }
          />
          <Route
            path="/contact"
            element={
              <PageWrapper>
                <Navbar />
                <Contact />
                <Footer />
              </PageWrapper>
            }
          />
          <Route
            path="/cart"
            element={
              <PageWrapper>
                <Navbar />
                <Cart />
                <Footer />
              </PageWrapper>
            }
          />
          <Route
            path="/checkout"
            element={
              <PageWrapper>
                <Navbar />
                <ErrorBoundary>
                  <Checkout />
                </ErrorBoundary>
                <Footer />
              </PageWrapper>
            }
          />
          <Route
            path="/privacy-policy"
            element={
              <PageWrapper>
                <Navbar />
                <PrivacyPolicy />
                <Footer />
              </PageWrapper>
            }
          />
          <Route
            path="/terms-of-service"
            element={
              <PageWrapper>
                <Navbar />
                <TermsOfService />
                <Footer />
              </PageWrapper>
            }
          />
          <Route
            path="*"
            element={
              <PageWrapper>
                <Navbar />
                <NotFound />
                <Footer />
              </PageWrapper>
            }
          />
        </Routes>
      </AnimatePresence>
      </Suspense>
    </>
  );
};

const WrappedApp = () => (
  <ErrorBoundary>
    <AuthProvider>
      <FarmProvider>
        <CartProvider>
          <CartDrawerProvider>
            <AuthPromptProvider>
              <Router
                future={{ v7_startTransition: true, v7_relativeSplatPath: true }}
              >
                <App />
                <CartButtonWrapper />
                <CartDrawer />
                <CartSheet />
                <AuthPromptDialog />
              </Router>
            </AuthPromptProvider>
          </CartDrawerProvider>
        </CartProvider>
      </FarmProvider>
    </AuthProvider>
  </ErrorBoundary>
);

export default WrappedApp;
